/**
 * Admin "Transcription Workflow" card: pick which workflow transcribes new recordings in a workspace.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2, Mic } from "lucide-react";
import {
  useWorkflows,
  useWorkspaceWorkflow,
  useSetWorkspaceWorkflow,
} from "../../hooks/useAdminWorkflows";
import { KiiConfirmDialog } from "../kii/kii-confirm-dialog";

interface WorkspaceWorkflowCardProps {
  workspaceId: string;
}

// callFunction prefixes the server text with its error code ("failed-precondition: ...").
const serverText = (message: string) => message.replace(/^[a-z-]+: /, "");

export function WorkspaceWorkflowCard({ workspaceId }: WorkspaceWorkflowCardProps) {
  const { t } = useTranslation();
  const registry = useWorkflows();
  const current = useWorkspaceWorkflow(workspaceId);
  const saveMutation = useSetWorkspaceWorkflow(workspaceId);
  const [selected, setSelected] = useState(current.data?.transcription_workflow ?? "");
  const [confirming, setConfirming] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (current.data) setSelected(current.data.transcription_workflow);
  }, [current.data]);

  function handleConfirm() {
    setConfirming(false);
    saveMutation.mutate(selected, {
      onSuccess: () => {
        setSaved(true);
        setTimeout(() => setSaved(false), 5000);
      },
    });
  }

  const loadFailed = registry.error || current.error;
  const loading = registry.isLoading || current.isLoading;
  const changed = !!current.data && selected !== current.data.transcription_workflow;
  const saveError = saveMutation.error ? serverText(saveMutation.error.message) : "";

  return (
    <div className="bg-card rounded-lg border border-border p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-orange-50 dark:bg-orange-950 rounded-lg">
          <Mic className="h-5 w-5 text-orange-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t("workspaceSettings.workflow.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("workspaceSettings.workflow.description")}</p>
        </div>
      </div>

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      ) : loadFailed ? (
        <p className="text-sm text-red-600">{t("workspaceSettings.workflow.loadError")}</p>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <select
            aria-label={t("workspaceSettings.workflow.select")}
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={saveMutation.isPending}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {registry.data?.workflows.map((w) => (
              <option key={w.id} value={w.id}>{w.label}</option>
            ))}
          </select>
          {current.data?.source === "default" && (
            <span className="text-xs text-muted-foreground">{t("workspaceSettings.workflow.default")}</span>
          )}
          <button
            type="button"
            disabled={!changed || saveMutation.isPending}
            onClick={() => setConfirming(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 rounded-lg transition-colors"
          >
            {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("common.save")}
          </button>
          {saved && (
            <span className="inline-flex items-center gap-1 text-sm text-green-600">
              <Check className="h-4 w-4" />
              {t("workspaceSettings.workflow.saved")}
            </span>
          )}
          {saveMutation.isError && (
            <span className="text-sm text-red-600">{saveError || t("workspaceSettings.workflow.saveError")}</span>
          )}
        </div>
      )}

      <KiiConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        onConfirm={handleConfirm}
        title={t("workspaceSettings.workflow.confirmTitle")}
        description={t("workspaceSettings.workflow.confirmDescription")}
        confirmText={t("workspaceSettings.workflow.confirmButton")}
        cancelText={t("common.cancel")}
      />
    </div>
  );
}
